import { useState } from 'react';
import { downloadPdfFromApi, openPdfFromApi } from '../lib/pdf';
import { ConfirmDialog } from './ConfirmDialog';

export interface PdfEntry {
  key: string;
  label: string;
  path: string;
  fileName: string;
  /** Quando presente, habilita a ação de remover (só faz sentido para PDFs de NF). */
  nfId?: number;
}

interface PdfViewerProps {
  entries: PdfEntry[];
  /** Chamado ao confirmar a remoção; a mutação/estado fica a cargo do pai. */
  onRemove?: (entry: PdfEntry) => void;
  removing?: boolean;
  emptyLabel?: string;
}

export function PdfViewer({ entries, onRemove, removing = false, emptyLabel }: PdfViewerProps) {
  const [err, setErr] = useState<string | null>(null);
  const [confirmEntry, setConfirmEntry] = useState<PdfEntry | null>(null);

  async function handleView(entry: PdfEntry) {
    setErr(null);
    try {
      await openPdfFromApi(entry.path, entry.fileName);
    } catch (e: any) {
      setErr(e?.message || 'PDF não encontrado.');
    }
  }

  async function handleDownload(entry: PdfEntry) {
    setErr(null);
    try {
      await downloadPdfFromApi(entry.path, entry.fileName);
    } catch (e: any) {
      setErr(e?.message || 'PDF não encontrado.');
    }
  }

  if (entries.length === 0) {
    return (
      <p className="py-2 text-center text-xs text-mbc-mute">
        {emptyLabel ?? 'Nenhum PDF anexado.'}
      </p>
    );
  }

  return (
    <div>
      <ul className="space-y-1.5">
        {entries.map(entry => (
          <li key={entry.key} className="flex items-center justify-between gap-2 rounded-lg bg-mbc-panel2 px-3 py-2">
            <span className="text-sm text-mbc-soft">📄 {entry.label}</span>
            <div className="flex gap-1.5">
              <button type="button" onClick={() => handleView(entry)} className="rounded bg-mbc-bluel/15 px-2.5 py-1 text-xs text-mbc-bluel hover:bg-mbc-bluel/30">Ver</button>
              <button type="button" onClick={() => handleDownload(entry)} className="rounded bg-emerald-500/15 px-2.5 py-1 text-xs text-emerald-300 hover:bg-emerald-500/30">Baixar</button>
              {entry.nfId != null && onRemove && (
                <button type="button" onClick={() => setConfirmEntry(entry)} disabled={removing} className="rounded bg-rose-500/15 px-2.5 py-1 text-xs text-rose-300 hover:bg-rose-500/30 disabled:opacity-40">Remover</button>
              )}
            </div>
          </li>
        ))}
      </ul>
      {err && <p className="mt-2 text-xs text-rose-300">{err}</p>}
      {confirmEntry && (
        <ConfirmDialog
          title="Remover PDF"
          message={`Remover o PDF da ${confirmEntry.label}? A NF não será excluída.`}
          confirmLabel="Remover"
          busy={removing}
          onConfirm={() => { onRemove?.(confirmEntry); setConfirmEntry(null); }}
          onCancel={() => setConfirmEntry(null)}
        />
      )}
    </div>
  );
}
